import { Alert, Box, CircularProgress } from '@mui/material';
import React, { useMemo } from 'react';
import ReactFlow, { Background, Controls, Edge, Node, Position, MarkerType, NodeTypes } from 'reactflow';
import 'reactflow/dist/style.css';
import { useGetFactoryNetworkQuery } from '../../redux/cutterHistoryAPI';
import CutterNodeWithWarning from './CutterNodeWithWarning';
import { SharedServiceNode, SmtpNode, ProxyNode, EndpointNode, ProductGroupNode } from './FactoryCustomNodes';

// Defined outside component to keep stable reference — prevents ReactFlow from re-creating the graph
const nodeTypes: NodeTypes = {
    cutterWithWarning: CutterNodeWithWarning,
    sharedServiceNode: SharedServiceNode,
    smtpNode: SmtpNode,
    proxyNode: ProxyNode,
    endpointNode: EndpointNode,
    productGroupNode: ProductGroupNode,
};

export interface CutterDetailsHistoryNetworkProps {
    serialNumber: string;
    width?: string | number;
    height?: string | number;
}

export const CutterDetailsHistoryNetwork: React.FC<CutterDetailsHistoryNetworkProps> = ({ serialNumber, width = '100%', height = '60vh' }) => {
    const { data, isLoading, isFetching, isError } = useGetFactoryNetworkQuery(serialNumber, {
        skip: !serialNumber
    });

    const nodes: Node[] = useMemo(() => {
        const mapped = (data?.nodes ?? []).map((node: any) => {
            // Map backend 'default' type to a specific custom node type based on node id prefix
            let type: string = node.type ?? 'default';
            if (type === 'default') {
                const id: string = node.id ?? '';
                if (id.startsWith('smtp:'))          type = 'smtpNode';
                else if (id.startsWith('proxy:'))    type = 'proxyNode';
                else if (id.startsWith('endpoint:')) type = 'endpointNode';
                else if (id.startsWith('shared:'))   type = 'sharedServiceNode';
            }
            return {
                ...node,
                type,
                position: node.position ?? { x: 0, y: 0 },
                data: {
                    ...(node.data ?? { label: node.id }),
                    machineType: node.data?.machineType,
                    machineProduct: node.data?.machineProduct,
                    machineMarket: node.data?.machineMarket,
                    measureDate: node.data?.measureDate,
                    softwareUpdateIndex: node.data?.softwareUpdateIndex,
                    connectionRate: node.data?.connectionRate
                },
                sourcePosition: Position.Top,
                targetPosition: Position.Bottom
            };
        });

        // Spread endpoint nodes horizontally to avoid overlap after chip-based width increase
        const ENDPOINT_SPACING = 230;
        const endpoints = mapped.filter((n: any) => n.type === 'endpointNode');
        if (endpoints.length > 1) {
            endpoints.sort((a: any, b: any) => a.position.x - b.position.x);
            const centerX = endpoints.reduce((sum: number, n: any) => sum + (n.position.x as number), 0) / endpoints.length;
            const totalWidth = (endpoints.length - 1) * ENDPOINT_SPACING;
            endpoints.forEach((n: any, i: number) => {
                n.position = { ...n.position, x: centerX - totalWidth / 2 + i * ENDPOINT_SPACING };
            });
        }

        // Push smtp and proxy nodes down so they don't overlap with endpoint (web server) nodes
        const SMTP_PROXY_Y_OFFSET = 60;
        mapped.filter((n: any) => n.type === 'smtpNode' || n.type === 'proxyNode').forEach((n: any) => {
            n.position = { ...n.position, y: (n.position.y as number) + SMTP_PROXY_Y_OFFSET };
        });

        // ── Group cutter nodes by MachineProduct into ReactFlow parent nodes ──
        const CUTTERS_PER_ROW = 4;
        const CELL_W          = 205;  // horizontal cell per cutter
        const CELL_H          = 175;  // vertical cell per cutter
        const PAD_H           = 14;   // left/right padding inside group
        const PAD_TOP         = 32;   // top padding (space for group label)
        const PAD_BOTTOM      = 14;
        const GROUP_SPACING   = 30;   // horizontal gap between groups
        const GROUP_Y         = 550;  // Y for all group nodes

        const cutterNodes = mapped.filter((n: any) => n.type === 'cutterWithWarning');
        const otherNodes  = mapped.filter((n: any) => n.type !== 'cutterWithWarning');

        // Build a map: machineProduct → cutter nodes (sorted by serial number)
        const groupMap = new Map<string, any[]>();
        for (const n of cutterNodes) {
            const product: string = (n.data?.machineProduct as string) || 'Unknown';
            if (!groupMap.has(product)) groupMap.set(product, []);
            groupMap.get(product)!.push(n);
        }
        const sortedGroups = Array.from(groupMap.entries()).sort(([a], [b]) => a.localeCompare(b));

        const groupNodeList: Node[] = [];
        const childNodeList: Node[] = [];
        let groupX = 30;

        for (const [product, cutters] of sortedGroups) {
            cutters.sort((a: any, b: any) =>
                ((a.data?.serialNumber as string) ?? '').localeCompare((b.data?.serialNumber as string) ?? ''));

            const rows    = Math.ceil(cutters.length / CUTTERS_PER_ROW);
            const maxCols = Math.min(cutters.length, CUTTERS_PER_ROW);
            // Extra half-cell width when odd rows are staggered
            const staggerExtra = rows > 1 ? CELL_W / 2 : 0;
            const groupW  = PAD_H * 2 + maxCols * CELL_W + staggerExtra;
            const groupH  = PAD_TOP + rows * CELL_H + PAD_BOTTOM;
            const groupId = `group:${product}`;

            groupNodeList.push({
                id: groupId,
                type: 'productGroupNode',
                position: { x: groupX, y: GROUP_Y },
                data: { label: product },
                style: {
                    width: groupW,
                    height: groupH,
                    borderRadius: '12px',
                    background: 'rgba(219,234,254,0.35)',
                    border: '2px dashed #93c5fd',
                },
                width: groupW,
                height: groupH,
            });

            cutters.forEach((n: any, i: number) => {
                const col = i % CUTTERS_PER_ROW;
                const row = Math.floor(i / CUTTERS_PER_ROW);
                // Stagger odd rows by half a cell to mirror the original layout
                const stagger = row % 2 === 1 ? CELL_W / 2 : 0;
                childNodeList.push({
                    ...n,
                    parentId: groupId,
                    extent: 'parent' as const,
                    position: {
                        x: PAD_H + stagger + col * CELL_W,
                        y: PAD_TOP + row * CELL_H,
                    },
                });
            });

            groupX += groupW + GROUP_SPACING;
        }

        // Group nodes must come before their children in the array
        return [...groupNodeList, ...childNodeList, ...otherNodes];
    }, [data]);

    const edges: Edge[] = useMemo(() => {
        return (data?.edges ?? []).map((edge: any) => {
            const processedEdge: any = {
                ...edge,
                animated: edge.animated ?? true
            };

            // Convert markerEnd type from backend string to ReactFlow MarkerType enum
            if (edge.markerEnd && typeof edge.markerEnd.type === 'string') {
                const markerType = edge.markerEnd.type.toLowerCase();
                processedEdge.markerEnd = {
                    ...edge.markerEnd,
                    type: markerType === 'arrowclosed' ? MarkerType.ArrowClosed : MarkerType.Arrow
                };
            }

            return processedEdge;
        });
    }, [data]);

    if (isLoading || isFetching) {
        return (
            <Box sx={{ minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (isError) {
        return <Alert severity="error">Unable to load factory network.</Alert>;
    }

    if (nodes.length === 0) {
        return <Alert severity="info">No network topology available for this cutter.</Alert>;
    }

    return (
        <Box sx={{ width, height }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable
            >
                <Controls />
                <Background />
            </ReactFlow>
        </Box>
    );
};

export default CutterDetailsHistoryNetwork;
